/**
 * The pieces Messaging's windows share (slice 2e's link window, slice 2h's
 * email window and Change): a dialog on a desktop and a sheet from the
 * bottom on a phone, a step's eyebrow, title and line, and a dialog's footer.
 */
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@crew/ui/components/alert-dialog";
import { Button } from "@crew/ui/components/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@crew/ui/components/dialog";
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from "@crew/ui/components/drawer";
import { cn } from "@crew/ui/lib/utils";
import { createContext, useContext, type ReactNode } from "react";
import { errorHint } from "../../app/api";
import { SOLID_RED } from "../settings/integrations-parts";
import { useRemoveLink } from "./api";

/** The frame the steps are in: their title and line are the dialog's or the sheet's. */
const Frame = createContext<"dialog" | "sheet" | null>(null);

/** A dialog on a desktop, a sheet on a phone (Paper 61–63, 71–75). */
export function WindowShell({
  open,
  mobile,
  onClose,
  children,
}: {
  open: boolean;
  mobile: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  if (mobile) {
    return (
      <Drawer open={open} onOpenChange={(o) => !o && onClose()} showSwipeHandle>
        <DrawerContent
          className="shadow-[0_-8px_30px_rgb(31_42_68/0.12)] data-[swipe-direction=down]:rounded-t-[18px] data-[swipe-direction=down]:border-t-0"
          showCloseButton
        >
          <Frame.Provider value="sheet">{children}</Frame.Provider>
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="gap-0 rounded-xl p-0 shadow-[0_24px_60px_rgb(31_42_68/0.18),0_2px_6px_rgb(31_42_68/0.08)] sm:max-w-[560px]"
        showCloseButton
      >
        <Frame.Provider value="dialog">{children}</Frame.Provider>
      </DialogContent>
    </Dialog>
  );
}

/** The step's eyebrow, title and line, as a dialog's or a sheet's (Paper 61–63). */
export function Head({
  mobile,
  eyebrow,
  tone = "copper",
  title,
  line,
}: {
  mobile: boolean;
  eyebrow: string;
  tone?: "copper" | "moss";
  title: string;
  line: ReactNode;
}) {
  const frame = useContext(Frame);
  const TitleEl = frame === "sheet" ? DrawerTitle : frame === "dialog" ? DialogTitle : "h2";
  const LineEl =
    frame === "sheet" ? DrawerDescription : frame === "dialog" ? DialogDescription : "p";
  return (
    <div className={cn("flex flex-col gap-1.5", mobile ? "px-5 pt-4.5" : "pt-6 pr-14 pl-7")}>
      <span
        className={cn(
          "text-xs font-medium tracking-label uppercase",
          tone === "moss" ? "text-moss" : "text-copper",
        )}
      >
        {eyebrow}
      </span>
      <TitleEl
        className={cn(
          "font-display font-normal tracking-tight text-foreground",
          mobile ? "text-[28px] leading-8" : "text-title leading-8.5",
        )}
      >
        {title}
      </TitleEl>
      {line ? <LineEl className="text-sm leading-5 text-muted-foreground">{line}</LineEl> : null}
    </div>
  );
}

/** A dialog's footer: a line on the left, its buttons on the right. */
export function Footer({ children, line }: { children: ReactNode; line?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-border px-7 py-4">
      <div className="flex min-w-0 items-center gap-2.5">{line}</div>
      <div className="flex shrink-0 gap-2">{children}</div>
    </div>
  );
}

/**
 * Unlink or Remove asks first (Paper 65, 76), and stays open until the link
 * is gone: a failed delete says why there, and the button can be pressed again.
 */
export function ConfirmRemove({
  linkId,
  open,
  onOpenChange,
  title,
  description,
  keep,
  action,
  marker,
}: {
  linkId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  /** The cancel button's words. */
  keep: string;
  action: string;
  /** The dialog's data attribute, for tests. */
  marker: string;
}) {
  const remove = useRemoveLink();
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (remove.isPending) return;
        if (next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent {...{ [marker]: "" }}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        {remove.error ? (
          <p role="alert" className="text-center text-meta text-destructive sm:text-left">
            {errorHint(remove.error)}
          </p>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>{keep}</AlertDialogCancel>
          <Button
            className={SOLID_RED}
            disabled={remove.isPending}
            onClick={() => remove.mutate(linkId, { onSuccess: () => onOpenChange(false) })}
          >
            {action}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
